import React, { useEffect } from 'react';
import { Settings, Eye, Save } from 'lucide-react';
import { Button, CardBody, Card } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { StepPropertiesFormData, createGuideBuilderSchemas } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormInput } from '../base';
import { useUpdateStep } from '@/hooks/useGuides';
import { getValidationMessages } from '../admin/guides/validationMessages';
import { useLanguage } from '@/contexts/LanguageContext';

const StepProperties: React.FC = () => {
  const {
    guide: { steps, id: guideId },
    state: { selectedStep },
    actions: { onPreviewToggle },
  } = useGuideBuilderGuide();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('admin');

  const updateStep = useUpdateStep(guideId || '');

  // Get translated validation messages and create schema with i18n
  const validationMessages = getValidationMessages(t);
  const { stepTitleSchema: stepTitleSchemaWithI18n } = createGuideBuilderSchemas(validationMessages);

  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty },
  } = useForm<StepPropertiesFormData>({
    resolver: zodResolver(stepTitleSchemaWithI18n),
    defaultValues: {
      title: selectedStep?.title || '',
    },
  });

  // Update form values when selectedStep or language changes
  useEffect(() => {
    if (selectedStep) {
      reset({
        title: selectedStep.title || '',
      });
    }
  }, [selectedStep, currentLanguage, reset]);

  const handleSave = handleSubmit(async (data) => {
    if (!selectedStep?.id) return;

    try {
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          title: data.title,
        },
      });
      // Reset form after successful save to mark as not dirty
      reset(data, { keepValues: true });
    } catch (error) {
      console.error('Error saving step:', error);
    }
  });

  const totalSteps = steps.length;
  const stepIndex = steps.findIndex((s) => s.id === selectedStep?.id);
  if (!selectedStep) {
    return (
      <Card className="m-6 flex-1">
        <CardBody>
          <h3 className="font-semibold text-foreground mb-4">{t('guides.builder.stepProperties.title')}</h3>
          <div className="text-center text-foreground">
            <Settings className="w-12 h-12 mx-auto mb-4 text-foreground" />
            <p className="text-sm text-foreground">{t('guides.builder.stepProperties.selectStep')}</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="m-6 flex-1">
      <CardBody>
        <h3 className="font-semibold text-foreground mb-4">{t('guides.builder.stepProperties.title')}</h3>

        <div className="space-y-4">
          <FormInput
            name="title"
            control={control}
            label={t('guides.builder.stepProperties.stepTitle')}
            placeholder={t('guides.builder.stepProperties.stepTitlePlaceholder')}
            isRequired
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('guides.builder.stepProperties.stepType')}
            </label>
            <div className="text-sm text-foreground capitalize">
              {t(`guides.builder.stepTypes.${selectedStep.type}`)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('guides.builder.stepProperties.order')}
            </label>
            <div className="text-sm text-foreground">
              {stepIndex + 1} of {totalSteps}
            </div>
          </div>

          {/* TODO: PDF URL input for PDF steps */}
          {/* {selectedStep.type === 'pdf' && (
          <Controller
            name={`steps.${stepIndex}.pdfUrl`}
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label={t('guides.builder.stepProperties.pdfUrl')}
                placeholder={t('guides.builder.stepProperties.pdfUrlPlaceholder')}
                type="url"
                errorMessage={errors.steps?.[stepIndex]?.pdfUrl?.message}
                isInvalid={!!errors.steps?.[stepIndex]?.pdfUrl}
                description={t('guides.builder.stepProperties.pdfUrlDescription')}
              />
            )}
          />
        )} */}

          {/* TODO: Video URL input for video steps - needs separate form/schema */}
          {/* {selectedStep.type === GuideStepType.VIDEO && (
            <FormVideoUrlInput
              name="content.videos"
              control={control}
              label={t('guides.builder.stepProperties.videoUrl')}
              placeholder={t('guides.builder.stepProperties.videoUrlPlaceholder')}
              type="url"
              description={t('guides.builder.stepProperties.videoUrlDescription')}
            />
          )} */}

          <div className="pt-4 border-t border-gray-200 space-y-2">
            {isDirty && (
              <Button
                onPress={() => handleSave()}
                color="primary"
                startContent={<Save className="w-4 h-4" />}
                className="w-full"
                isLoading={updateStep.isPending}
              >
                {t('guides.builder.stepProperties.save')}
              </Button>
            )}
            <Button
              onPress={onPreviewToggle}
              variant="bordered"
              startContent={<Eye className="w-4 h-4" />}
              className="w-full"
            >
              {t('guides.builder.stepEditor.previewStep')}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default StepProperties;
